<template>
	<div class="product-container">
		<template v-for="step in filteredWorkflowSteps">
			<product-container 
				v-for="(product, index) in step.productContainer" 
				:product="product" 
				:step="step"
				:country="country" 
				v-bind="$props" 
				:stepNumber="step.stepNumber"
				@productTotalPrice="productTotalPrice($event)" 
				:genomic="genomic" 
				:key="product.key"
				:last-step="lastStep" 
				:productIndex="index" />
		</template>
		<div v-if="!genomic" class="products-total-price headline">
			<span class="body-2">Total</span>
			<transition mode="out-in" name="fade-in">
				<span> {{ formatPrice(totalPrice, currency) }}</span>
			</transition>
		</div>
	</div>
</template>

<script>
import ProductContainer from '@/components/modals/requestsModalComponents/ProductContainer.vue';
import priceFormatter from '@/mixins/priceFormatter';
import stepProductMethods from '@/components/workflow/mixins/stepProductMethods';

export default {
	name: 'ProductsView',
	props: {
		workflowSteps: {
			type: Array,
		},
		country: {
			type: String,
			require: true,
			default: 'US',
		},
		genomic: {
			type: Boolean,
			default: false,
		},
	},
	mixins: [priceFormatter, stepProductMethods],
	components: { ProductContainer },
	data() {
		return {
			priceList: null,
			selectedProducts: [],
			productQuantity: null,
			currency: 'USD'
		};
	},
	created() {
		this.currency = this.setCurrency();
		this.priceList = this.setPriceList();
		this.selectedProducts = this.setSelectedProducts();
		this.productQuantity = this.initQuantityPerProduct();
	},
	mounted() {
		this.findGeneGlobeSteps();
	},
	computed: {
		filteredWorkflowSteps() {
			return this.workflowSteps
				.map((workflow, index) => ({ ...workflow, stepNumber: this.setStep(index) }))
				.filter(
					(step) =>
						!this.isCustomWorkStep(step) &&
						!this.isCustomProduct(step) &&
						!this.isGeneGlobeProduct(step) &&
						step.stepEnabled,
				)
				.map((step) => Object.assign(step, { productContainer: this.findSelectedProducts(step) }))
				.flat();
		},

		lastStep() {
			return Math.max(...this.filteredWorkflowSteps.map((step) => step.stepNumber));
		},
		productList() {
			if (!this.filteredWorkflowSteps) return;
			return this.filteredWorkflowSteps.map((step) => {
				const selectedProducts = this.findSelectedProducts(step).map((product) => ({
					...product,
					stepNumber: step.stepNumber,
				}));
				return { selectedProducts, stepNumber: step.stepNumber };
			});
		},
		getPriceByCatalogNum() {
			const priceLIst = this.productList.map(({ selectedProducts, stepNumber }) => {
				const result = selectedProducts.map((product) => {
					const price = this.getPrice(product.prices, product.priceStatuses[this.country]);
					return { ...price, prodKey: `${product.catalogNumber}-${stepNumber}` };
				});

				return result;
			});

			return priceLIst.flat();
		},

		totalPrice() {
			if (!this.priceList) return;
			const sumTotalPrice = (sum, [key, value]) => sum + value;
			return Object.entries(this.priceList).reduce(sumTotalPrice, 0);
		},
		someProductsSelected() {
			return Object.values(this.selectedProducts).some((value) => value);
		},
	},
	watch: {
		someProductsSelected(newValue) {
			this.$emit('preventNextStep', newValue);
		},
	},
	methods: {
		setPriceList() {
			function priceByProductKey(acc, { prodKey, value, custom }) {
				const val = custom || value;
				return { ...acc, [prodKey]: val || 0 };
			}
			return this.getPriceByCatalogNum.reduce(priceByProductKey, {});
		},
		setCurrency() {
			return this.getPriceByCatalogNum[0].currencyIsoCode;
		},

		setSelectedProducts() {
			const isSelected = (acc, product) => ({ ...acc, [`${product.catalogNumber}-${product.stepNumber}`]: true });
			const result = this.productList.map((product) => product.selectedProducts.reduce(isSelected, {}));
			return result.reduce((acc, product) => ({ ...acc, ...product }));
		},

		initQuantityPerProduct() {
			const getInitProduct = (acc, step) => {
				const selectedProducts = this.findSelectedProducts(step);
				const catStep = selectedProducts.map(({ catalogNumber }) => {
					return { [`${catalogNumber}-${step.stepNumber}`]: 1 };
				});

				return Object.assign(acc, ...catStep);
			};
			return this.filteredWorkflowSteps.reduce(getInitProduct, {});
		},

		findSelectedProducts({ products, selected }) {
			return products.filter((product) =>
				selected.some(({ updated }) => !updated.includes('custom-product') && updated.includes(product.key)),
			);
		},

		findGeneGlobeSteps() {
			for (const step of this.workflowSteps) {
				step.isGeneGlobe = this.isGeneGlobeProduct(step);
			}
		},

		productTotalPrice({ total, key, selected, qty, catNumber }) {
			this.priceList[key] = total;
			this.selectedProducts[key] = selected;
			this.productQuantity[catNumber] = qty;
		},

		setStep(index) {
			return ++index;
		},

		getPrice(prices, status) {
			if (!prices) return;
			// return flitered for price based on country
			const price = prices.filter((price) => price.countryIsoCode === this.country)[0].price;
			return {
				Display: price, // price.value or .custom or null = no price shown
				Inquire: null, // no price shown
				Login: price, // user has to be logged into see this
				CannotOrderOnline: price && price.custom ? { value: price.value, currencyIsoCode: price.currencyIsoCode } : price, // if price and contains custom, only show value. or null
				ComingSoon: null, // no price shown
			}[status];
		},
	},
};
</script>

<style lang="scss" scoped>
.product-container {
	max-width: 1236px;
	width: 100%;
	border: 1px solid $color-light-blue;

	.products-total-price {
		@include flexRow;
		align-items: center;
		justify-content: flex-end;
		padding: 20px 16px;

		span:nth-child(1) {
			margin-right: 34px;
		}
	}

	.fade-in-enter-active {
		transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
	}

	.fade-in-leave-active {
		transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
	}

	.fade-in-enter,
	.fade-in-leave-to {
		opacity: 0;
	}
}

.fade-in-enter-active {
	transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.fade-in-leave-active {
	transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-in-enter,
.fade-in-leave-to {
	opacity: 0;
}
</style>
